import styles from '../Dashboard.module.scss';
import { TpDataContext } from '../../../../data/interfaces';
import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLoadViewState } from '../../hooks/useLoadViewState';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import {
  copyDashboardState,
  dashboardStateClipboardHasCompatibleData,
  getCompressedDashboardState,
} from '../../../../data/snapshot/dashboardStateClipboard';
import { TpAssetInfo } from '../../../../data/assets/interfaces';
import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import { sendEvent } from '../../hooks/events';
import { useViewState } from '../../hooks/ViewStateContext';
import { promptAutoForm } from '../../../../components/auto-form-modal/AutoForm';
import { saveViewStateForm } from './saveViewStateForm';
import { selectTemplateForm } from './selectTemplateForm';
import { VIEW_STATE_TERMS } from '../../../../data/snapshot/dashboardStateSerialization';
import { useAppSelector, useUser } from '../../../../app/hooks';
import { selectDashboardDefs } from '../../data/dashboardsSlice';
import { selectDashboardTemplates } from '../../../../data/templateSlice';
import {
  getAvailableDashboardTemplatesForAsset,
  resolveTemplateForAsset,
} from '../../../../data/templateSlice.helpers';
import { TpDashboardDefinition } from '../interfaces';
import { TpTemplateInfo } from '../../../../api/models/assets';
import { useSelectDashboardTemplate } from '../../hooks/useSelectDashboardTemplate';
import { SaveDashboardAsTemplateButton } from './SaveDashboardAsTemplateButton';

export interface TpTopBarMenuPopupProps {
  dataContext: TpDataContext;
  assetInfo: TpAssetInfo;
  close: () => void;
}

export function TopBarMenu({ dataContext, assetInfo, close }: TpTopBarMenuPopupProps) {
  const { user } = useUser();
  const selectDashboardTemplate = useSelectDashboardTemplate();
  const { loadViewStateFromCompressedState } = useLoadViewState();
  const { viewStates, activeViewStateId, fetchViewStateList, saveViewState } = useViewState();

  const dashboardDefs = useAppSelector(selectDashboardDefs).dashboardDefs;
  const dashboardDef: TpDashboardDefinition | undefined = dashboardDefs[assetInfo.id];
  const dashboardTemplates = useAppSelector(selectDashboardTemplates);
  const userIsAdmin = user?.isOrganizationAdmin ?? false;
  const canSaveDashboardAsTemplate = userIsAdmin && Boolean(dashboardDef?.templateInfo);

  const availableTemplates = useMemo(
    () => getAvailableDashboardTemplatesForAsset(dashboardTemplates, assetInfo.type, assetInfo.id, userIsAdmin),
    [dashboardTemplates, assetInfo.type, assetInfo.id, userIsAdmin],
  );

  const [isReplaying, setIsReplaying] = useState(false);
  const [isPreparingSave, setIsPreparingSave] = useState(false);

  const updateSelectTemplate = (selectedTemplate: TpTemplateInfo, selectedTemplateId: string | null) => {
    try {
      selectDashboardTemplate(selectedTemplate, dataContext, assetInfo.id, selectedTemplateId);
    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : String(error));
      throw error;
    }
  };

  const handleSelectTemplate = (selectedTemplate: TpTemplateInfo) => {
    updateSelectTemplate(selectedTemplate, selectedTemplate.id ?? null);
  };

  const handleRevertDefault = () => {
    const defaultTemplate =
      resolveTemplateForAsset(dashboardTemplates?.[assetInfo.type], assetInfo.id, 'dashboard') ||
      assetInfo.assetDashboardTemplate;
    if (defaultTemplate) {
      updateSelectTemplate(defaultTemplate, null);
    }
  };

  function performSelectTemplate() {
    close();
    void promptAutoForm(
      selectTemplateForm({
        templates: availableTemplates,
        currentTemplateId: dashboardDef?.templateInfo?.id,
        onSelectTemplate: handleSelectTemplate,
        onRevertDefault: handleRevertDefault,
        close,
      }),
    ).catch(close);
  }

  function performCopyDashboardState() {
    close();
    copyDashboardState(dataContext, assetInfo, dashboardDef).catch((error) => console.error(error));
  }

  async function performPasteDashboardState() {
    const stopPaste = () => {
      setIsReplaying(false);
      close();
    };

    setIsReplaying(true);
    try {
      const compressedDashboardState = getCompressedDashboardState(assetInfo);
      if (!compressedDashboardState) {
        console.error('No compatible dashboard state found in clipboard.');
        stopPaste();
        return;
      }

      await loadViewStateFromCompressedState(
        compressedDashboardState,
        dataContext,
        dashboardDef?.templateInfo.id ?? '',
      );
      stopPaste();
      return;
    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : String(error));
      console.error('Error pasting dashboard state:', error);
    } finally {
      stopPaste();
    }
  }

  async function performSaveDashboardState() {
    setIsPreparingSave(true);
    try {
      const fetchedViewStates = await fetchViewStateList(true);
      close();
      void promptAutoForm(
        saveViewStateForm({
          close,
          viewStates: fetchedViewStates ?? viewStates,
          saveViewState,
          activeViewStateId,
        }),
      ).catch(close);
    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : String(error));
      close();
    } finally {
      setIsPreparingSave(false);
    }
  }

  return (
    <div className={styles.menuPopup}>
      {isReplaying && (
        <div className={styles.replayOverlay} role="status" aria-label={`Replaying ${VIEW_STATE_TERMS.singular}`}>
          <FontAwesomeIcon icon={['fas', 'spinner']} spin className={styles.replaySpinner} />
        </div>
      )}
      <FancyButton iconName="clipboard" onClick={performCopyDashboardState}>
        Copy {VIEW_STATE_TERMS.singular}
      </FancyButton>
      {dashboardStateClipboardHasCompatibleData(assetInfo) && (
        <FancyButton iconName="paste" onClick={performPasteDashboardState}>
          Paste {VIEW_STATE_TERMS.singular}
        </FancyButton>
      )}
      <FancyButton
        iconName="bookmark"
        onClick={performSaveDashboardState}
        disabled={isPreparingSave}
        isLoading={isPreparingSave}
      >
        {isPreparingSave ? `Loading ${VIEW_STATE_TERMS.plural}...` : `Save ${VIEW_STATE_TERMS.singular}...`}
      </FancyButton>
      <FancyButton
        iconName="folder-open"
        onClick={() => {
          close();
          sendEvent('ShowViewStateSelector', null);
        }}
      >
        Load {VIEW_STATE_TERMS.singular}...
      </FancyButton>
      {availableTemplates.length > 1 && (
        <FancyButton iconName="layer-group" onClick={performSelectTemplate}>
          Select dashboard...
        </FancyButton>
      )}
      {canSaveDashboardAsTemplate && dashboardDef && (
        <div className={styles.menuPopupSection}>
          <SaveDashboardAsTemplateButton
            dataContext={dataContext}
            assetInfo={assetInfo}
            dashboardDef={dashboardDef}
            closeMenu={close}
          />
        </div>
      )}
    </div>
  );
}
